import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CInputGroup,
  CInputCheckbox,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItem,
  getList,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import Loading from "../indicators/Loading";
import TaxPayerUser, { newTaxPayerUser } from "../../models/tax-payer-user";
import { useSelector } from "react-redux";
import {
  ACCOUNTING_ENTRIES_GROUP_LABEL,
  AI_GROUP_LABEL,
  CONNECTIVITY_GROUP_LABEL,
  PERMISSION_GROUPS,
} from "../../auth/permissions";
import { RootState } from "../../store";
import EstablishmentMultiSelect from "../establishment/EstablishmentMultiSelect";
import Establishment from "../../models/establishment";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";

interface UserFormProps {
  initialUser?: TaxPayerUser;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const UserForm: React.FC<UserFormProps> = ({
  initialUser,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<TaxPayerUser>(
    initialUser ? initialUser : newTaxPayerUser()
  );

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);

  const taxPayerId = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.id
  );
  const connectivityAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.connectivityAccess
  );
  const accountingAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.accountingAccess
  );
  const aiAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.aiAccess
  );
  const aiEnabled = useSelector(
    (state: RootState) => state.taxPayerParameters.data.aiEnabled
  );

  const onFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      firstName: e.target.value,
    });
  };

  const onLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      lastName: e.target.value,
    });
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      email: e.target.value,
    });
  };

  const onPassword1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      password1: e.target.value,
    });
  };

  const onPassword2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      password2: e.target.value,
    });
  };

  const onPassword1ShowClick = () => {
    setShowPassword1(!showPassword1);
  };

  const onPassword2ShowClick = () => {
    setShowPassword2(!showPassword2);
  };

  const onPermissionsChange = useCallback(
    (permissionName: string, enabled: boolean) => {
      const newEditingUser = { ...editingUser };
      if (newEditingUser.permissions === undefined) {
        newEditingUser.permissions = [];
      }

      const permissionIndex =
        newEditingUser.permissions.indexOf(permissionName);

      if (enabled && permissionIndex < 0) {
        newEditingUser.permissions.push(permissionName);
      }

      if (!enabled && permissionIndex >= 0) {
        const permissionIndex =
          newEditingUser.permissions.indexOf(permissionName);
        newEditingUser.permissions.splice(permissionIndex, 1);
      }

      setEditingUser(newEditingUser);
    },
    [editingUser]
  );

  const onAllEstablishmentsChange = (value: boolean) => {
    setEditingUser({
      ...editingUser,
      allEstablishments: value,
    });
  };

  const onIsSuperUserChange = (value: boolean) => {
    setEditingUser({
      ...editingUser,
      isSuperUser: value,
    });
  };

  const onEstablishmentsChange = (selectedEstablishments: Establishment[]) => {
    setEstablishments(selectedEstablishments);
    setEditingUser({
      ...editingUser,
      establishmentIds: selectedEstablishments.map((e) => e.id!),
    });
  };

  const onSave = useCallback(async () => {
    setSubmitting(true);
    let toSendUser = {
      ...editingUser,
      taxPayerId: taxPayerId,
    };

    if (toSendUser.allEstablishments === undefined) {
      toSendUser.allEstablishments = true;
    }

    let requestPromise: Promise<ItemRequestStatus<TaxPayerUser>>;
    if (editingUser.id === undefined) {
      requestPromise = createItem<TaxPayerUser>(
        `/tax_payer_users/${taxPayerId}/users/create/`,
        toSendUser
      );
    } else {
      requestPromise = updateItem<TaxPayerUser>(
        `/tax_payer_users/${taxPayerId}/users/${toSendUser.id}/`,
        toSendUser
      );
    }

    const userStatus = await requestPromise;

    if (userStatus.status !== SUCCESS) {
      if (userStatus.errors !== undefined) {
        setErrors(userStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (userStatus.detail !== undefined) {
        message = userStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayerId, editingUser]);

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingUser(newTaxPayerUser());
    setEstablishments([]);
  };

  const loadUser = useCallback(async () => {
    if (initialUser === undefined || initialUser.id === undefined) {
      setEditingUser(newTaxPayerUser());
      setEstablishments([]);
      return;
    }

    setLoading(true);

    const requestStatus = await getItem<TaxPayerUser>(
      `/tax_payer_users/${taxPayerId}/users/${initialUser.id}/`
    );

    if (requestStatus.status !== SUCCESS || requestStatus.data === undefined) {
      setEditingUser(initialUser);
      setLoading(false);
      return;
    }

    setEditingUser(requestStatus.data);

    // Load establishments for this user if editing
    const establishmentsResponse = await getList<Establishment>(
      `/tax_payer_users/${taxPayerId}/users/${initialUser.id}/establishments/`,
      1000,
      0
    );

    if (
      establishmentsResponse.status === SUCCESS &&
      establishmentsResponse.data !== undefined
    ) {
      const loadedEstablishments = establishmentsResponse.data.items;
      setEstablishments(loadedEstablishments);
      setEditingUser((prev) => ({
        ...prev,
        establishmentIds: loadedEstablishments.map((e) => e.id!),
      }));
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayerId, initialUser]);

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUser]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingUser.firstName)}
                onChange={onFirstNameChange}
                placeholder="Ej: Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("firstName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Apellido</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingUser.lastName)}
                onChange={onLastNameChange}
                placeholder="Ej: Apellido"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("lastName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Correo electrónico</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingUser.email)}
                onChange={onEmailChange}
                placeholder="Ej: Correo electrónico"
                autoComplete="new-email"
                readOnly={initialUser?.id !== undefined}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("email", errors) as string[]}
              ></FieldErrors>
              <FieldErrors
                errors={getFieldErrors("username", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        {initialUser?.id !== undefined ? (
          <></>
        ) : (
          <>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Contraseña</CLabel>
                </CCol>
                <CCol>
                  <CInputGroup>
                    <CInput
                      type={showPassword1 ? "text" : "password"}
                      value={emptyValueOnUndefined(editingUser.password1)}
                      onChange={onPassword1Change}
                      placeholder="Ej: Contraseña"
                      autoComplete="new-password"
                    ></CInput>
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onPassword1ShowClick}
                    >
                      {showPassword1 ? (
                        <i className="fa fa-eye-slash"></i>
                      ) : (
                        <i className="fa fa-eye"></i>
                      )}
                    </CButton>
                  </CInputGroup>
                  <FieldErrors
                    errors={getFieldErrors("password1", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Repita la Contraseña</CLabel>
                </CCol>
                <CCol>
                  <CInputGroup>
                    <CInput
                      type={showPassword2 ? "text" : "password"}
                      value={emptyValueOnUndefined(editingUser.password2)}
                      onChange={onPassword2Change}
                      placeholder="Ej: Repita la Contraseña"
                      autoComplete="new-password"
                    ></CInput>
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onPassword2ShowClick}
                    >
                      {showPassword2 ? (
                        <i className="fa fa-eye-slash"></i>
                      ) : (
                        <i className="fa fa-eye"></i>
                      )}
                    </CButton>
                  </CInputGroup>
                  <FieldErrors
                    errors={getFieldErrors("password2", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
          </>
        )}

        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Super Usuario</CLabel>
            </CCol>
            <CCol md={10}>
              <AnimatedCheckbox
                value={editingUser.isSuperUser}
                onChange={onIsSuperUserChange}
              />
            </CCol>
          </CRow>
        </CFormGroup>

        {!editingUser.isSuperUser && (
          <>
            <CRow className="mb-2">
              <CCol>
                <h5>
                  <u>Permisos</u>
                </h5>
              </CCol>
            </CRow>
            <CFormGroup>
              {Array.from(PERMISSION_GROUPS.entries())
                .filter(
                  ([groupLabel]) =>
                    !(
                      groupLabel === CONNECTIVITY_GROUP_LABEL &&
                      connectivityAccess === false
                    ) &&
                    !(
                      groupLabel === ACCOUNTING_ENTRIES_GROUP_LABEL &&
                      accountingAccess !== true
                    ) &&
                    // If SmartDoc has not enabled the channel for this company
                    // the permissions must not even be offered: a switch that
                    // does nothing when ticked is worse than no switch.
                    !(groupLabel === AI_GROUP_LABEL && aiAccess !== true)
                )
                .map((entry) => (
                <CRow key={entry[0]}>
                  <CCol md={2}>
                    <CLabel>{entry[0]}</CLabel>
                    {entry[0] === AI_GROUP_LABEL && aiEnabled === false && (
                      // Sold, but the company turned the channel off: the
                      // permission is real and still has no effect, so say so
                      // instead of letting somebody tick it and open a ticket.
                      <div>
                        <small className="text-muted">
                          La IA está desactivada en esta empresa
                        </small>
                      </div>
                    )}
                  </CCol>
                  <CCol md={10}>
                    <CRow>
                      {entry[1].map((value) => (
                        <CCol key={value.value} md={3}>
                          <CInputCheckbox
                            id={`permission_${value.value}`}
                            checked={
                              editingUser.permissions !== undefined &&
                              editingUser.permissions.indexOf(value.value) >= 0
                            }
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              onPermissionsChange(
                                value.value,
                                e.target.checked
                              );
                            }}
                          ></CInputCheckbox>
                          <CLabel htmlFor={`permission_${value.value}`}>
                            {value.label}
                          </CLabel>
                        </CCol>
                      ))}
                    </CRow>
                  </CCol>
                </CRow>
              ))}
            </CFormGroup>

            <CRow className="mb-2 mt-4">
              <CCol>
                <h5>
                  <u>Establecimientos</u>
                </h5>
              </CCol>
            </CRow>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Todos los Establecimientos</CLabel>
                </CCol>
                <CCol md={10}>
                  <AnimatedCheckbox
                    value={editingUser.allEstablishments}
                    onChange={onAllEstablishmentsChange}
                  />
                </CCol>
              </CRow>
            </CFormGroup>
            {!editingUser.allEstablishments && (
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Establecimientos</CLabel>
                  </CCol>
                  <CCol md={10}>
                    <EstablishmentMultiSelect
                      value={establishments}
                      onChange={onEstablishmentsChange}
                      disabled={editingUser.allEstablishments}
                    />
                    <FieldErrors
                      errors={
                        getFieldErrors("establishmentIds", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
            )}
          </>
        )}

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default UserForm;
